import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { baixarArquivo, requisitar, salvarArquivo } from './client.ts';
import type {
  Agente,
  Contabilidade,
  DadosDoPortal,
  Desfecho,
  Empresa,
  FiltroDaSituacaoMensal,
  Pagina,
  PaginaDeArquivosFiscais,
  PaginaDeEnvios,
  Papel,
  ResultadoDaConferencia,
  ResultadoEnvioPorEmail,
  Sessao,
  SequenciaDoCliente,
  SituacaoDeEnvio,
  SituacaoDoMes,
  SituacaoFiscal,
  TriagemDePendencias,
  Usuario,
} from './tipos.ts';

/**
 * Chaves de cache centralizadas. Espalhar arrays literais pelos componentes é
 * a forma mais rápida de acabar com uma invalidação que não casa com nada.
 */
export const chaves = {
  clientes: () => ['clientes'] as const,
  contabilidades: () => ['contabilidades'] as const,
  usuarios: (contabilidadeId?: string) => ['usuarios', contabilidadeId ?? null] as const,
  arquivosFiscais: (filtro: FiltroArquivosFiscais) => ['arquivos-fiscais', filtro] as const,
  agentes: () => ['agentes'] as const,
  enviosDeEmail: (filtro: Record<string, unknown>) => ['envios-de-email', filtro] as const,
  pendencias: (filtro: Record<string, unknown>) => ['pendencias', filtro] as const,
  arquivosPendentes: (filtro: Record<string, unknown>) => ['pendencias-arquivos', filtro] as const,
  sequencia: (filtro: Record<string, unknown>) => ['pendencias-sequencia', filtro] as const,
  triagem: (filtro: Record<string, unknown>) => ['pendencias-codigos', filtro] as const,
};

/**
 * No domínio e na API o agregado é `Empresa` — é o termo da Receita. Para o
 * escritório, cada empresa atendida é um cliente. A tradução entre as duas
 * linguagens acontece aqui, e não espalhada pelas telas.
 */
export function useClientes() {
  return useQuery({
    queryKey: chaves.clientes(),
    queryFn: ({ signal }) =>
      requisitar<Pagina<Empresa>>('/empresas', { parametros: { limite: 500 }, sinal: signal }),
    staleTime: 60_000,
  });
}

export interface EntradaCliente {
  readonly razaoSocial: string;
  readonly cnpj: string;
  /** Lista completa: o servidor substitui o que estava lá. */
  readonly emails: readonly string[];
}

export function useCadastrarCliente() {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: (entrada: EntradaCliente) =>
      requisitar<Empresa>('/empresas', { metodo: 'POST', corpo: entrada }),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: chaves.clientes() });
    },
  });
}

export interface EntradaEditarCliente extends EntradaCliente {
  readonly id: string;
  /** Desmarcar inativa o cliente; o cadastro e o histórico permanecem. */
  readonly ativa: boolean;
}

export function useEditarCliente() {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...campos }: EntradaEditarCliente) =>
      requisitar<Empresa>(`/empresas/${id}`, { metodo: 'PATCH', corpo: campos }),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: chaves.clientes() });
    },
  });
}

/**
 * Guarda o certificado digital do cliente.
 *
 * Vai como `multipart`, e não como JSON com o arquivo em base64: é o mesmo
 * caminho do envio de XML do agente, e é o que deixa o limite de tamanho ser
 * aplicado pelo servidor antes de o corpo inteiro entrar na memória.
 */
export function useGuardarCertificado() {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: ({ id, arquivo, senha }: { id: string; arquivo: File; senha: string }) => {
      const corpo = new FormData();
      corpo.append('senha', senha);
      corpo.append('arquivo', arquivo, arquivo.name);

      return requisitar<Empresa>(`/empresas/${id}/certificado`, {
        metodo: 'POST',
        corpoBruto: corpo,
      });
    },
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: chaves.clientes() });
    },
  });
}

export function useRemoverCertificado() {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      requisitar<Empresa>(`/empresas/${id}/certificado`, { metodo: 'DELETE' }),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: chaves.clientes() });
    },
  });
}

/**
 * Baixa o `.pfx` guardado. Rota de administrador, e autenticada — por isso
 * passa por `baixarArquivo` e não por um `<a href>`.
 */
export async function baixarCertificado(id: string): Promise<void> {
  salvarArquivo(await baixarArquivo(`/empresas/${id}/certificado`));
}

export function useContabilidades() {
  return useQuery({
    queryKey: chaves.contabilidades(),
    queryFn: ({ signal }) =>
      requisitar<Pagina<Contabilidade>>('/contabilidades', {
        parametros: { limite: 200 },
        sinal: signal,
      }),
    staleTime: 60_000,
  });
}

export interface EntradaContabilidade {
  readonly razaoSocial: string;
  readonly cnpj: string;
  readonly emails: readonly string[];
}

export function useCadastrarContabilidade() {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: (entrada: EntradaContabilidade) =>
      requisitar<Contabilidade>('/contabilidades', { metodo: 'POST', corpo: entrada }),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: chaves.contabilidades() });
    },
  });
}

export interface EntradaEditarContabilidade extends EntradaContabilidade {
  readonly id: string;
  readonly ativa: boolean;
}

export function useEditarContabilidade() {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...campos }: EntradaEditarContabilidade) =>
      requisitar<Contabilidade>(`/contabilidades/${id}`, { metodo: 'PATCH', corpo: campos }),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: chaves.contabilidades() });
    },
  });
}

export interface ResultadoVinculo {
  clientes: Empresa[];
  vinculados: number;
  desvinculados: number;
  transferidos: number;
}

/**
 * Define a lista completa de clientes da contabilidade. Invalida também os
 * clientes: o vínculo mora neles, e a tela de clientes mostra a contabilidade.
 */
export function useDefinirClientesDaContabilidade() {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: ({ id, clienteIds }: { id: string; clienteIds: readonly string[] }) =>
      requisitar<ResultadoVinculo>(`/contabilidades/${id}/clientes`, {
        metodo: 'PUT',
        corpo: { clienteIds },
      }),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: chaves.clientes() });
      void cache.invalidateQueries({ queryKey: chaves.contabilidades() });
    },
  });
}

/* --------------------------------- usuários -------------------------------- */

/**
 * Só administrador tem acesso; a tela nem aparece no menu dos outros.
 *
 * Com `contabilidadeId`, traz os contadores daquela contabilidade — é o que a
 * tela da contabilidade lista, sem carregar o cadastro inteiro de usuários.
 */
export function useUsuarios(contabilidadeId?: string, habilitado = true) {
  return useQuery({
    queryKey: chaves.usuarios(contabilidadeId),
    queryFn: ({ signal }) =>
      requisitar<Pagina<Usuario>>('/usuarios', {
        parametros: { limite: 200, contabilidadeId },
        sinal: signal,
      }),
    staleTime: 60_000,
    enabled: habilitado,
  });
}

export interface EntradaUsuario {
  readonly nome: string;
  readonly usuario: string;
  readonly senha: string;
  readonly papel: Papel;
  /** Obrigatório no papel `contador`. */
  readonly contabilidadeId?: string | undefined;
}

export function useCadastrarUsuario() {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: (entrada: EntradaUsuario) =>
      requisitar<Usuario>('/usuarios', { metodo: 'POST', corpo: entrada }),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: ['usuarios'] });
    },
  });
}

export interface EntradaEditarUsuario {
  readonly id: string;
  readonly nome: string;
  readonly usuario: string;
  readonly papel: Papel;
  readonly contabilidadeId?: string | undefined;
  readonly ativo: boolean;
  /** Ausente mantém a senha; presente redefine e derruba as sessões dele. */
  readonly senha?: string | undefined;
}

export function useEditarUsuario() {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: ({ id, senha, ...campos }: EntradaEditarUsuario) =>
      requisitar<Usuario>(`/usuarios/${id}`, {
        metodo: 'PATCH',
        corpo: senha === undefined || senha === '' ? campos : { ...campos, senha },
      }),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: ['usuarios'] });
    },
  });
}

/**
 * Troca da própria senha. Devolve uma sessão nova porque o servidor invalida o
 * token anterior — quem chama precisa guardar o token que volta.
 */
export function useAlterarPropriaSenha() {
  return useMutation({
    mutationFn: (entrada: { senhaAtual: string; novaSenha: string }) =>
      requisitar<Sessao>('/auth/senha', {
        metodo: 'POST',
        corpo: entrada,
        trataProprio401: true,
      }),
  });
}

/* ------------------------------ arquivos fiscais --------------------------- */

export interface FiltroArquivosFiscais {
  /** Ausente traz as duas, que é como a tela abre. */
  readonly situacao?: SituacaoFiscal | undefined;
  /** Vazio traz todos os clientes. */
  readonly empresaId?: string | undefined;
  /** `AAAA-MM-DD`, as duas pontas incluídas. */
  readonly de?: string | undefined;
  readonly ate?: string | undefined;
  /**
   * Identificação da nota, um campo por pergunta. Vão como texto porque é
   * texto que a pessoa tem na mão — a chave copiada da tela, com os espaços do
   * agrupamento, e o número do relatório do PDV, com zeros à esquerda. Quem
   * normaliza é o servidor, e é lá que a regra mora.
   */
  readonly chave?: string | undefined;
  readonly numero?: string | undefined;
  readonly serie?: string | undefined;
}

export interface PaginaPedida {
  readonly limite: number;
  readonly deslocamento: number;
}

/**
 * Uma página de arquivos fiscais, não o período inteiro.
 *
 * Um cliente de PDV fecha o mês com dezenas de milhares de cupons, e trazer
 * tudo de uma vez não trava só o navegador: o `.json` da resposta e a tabela
 * montada a partir dele crescem juntos. O servidor já pagina — o que a tela
 * faz é pedir uma página por vez e deixar o resumo (que é do filtro inteiro)
 * responder pelo total.
 */
export function useArquivosFiscais(filtro: FiltroArquivosFiscais, pagina: PaginaPedida) {
  return useQuery({
    queryKey: chaves.arquivosFiscais({ ...filtro, ...pagina }),
    queryFn: ({ signal }) =>
      requisitar<PaginaDeArquivosFiscais>('/arquivos-fiscais', {
        parametros: { ...filtro, ...pagina },
        sinal: signal,
      }),
    // A consulta é sempre de um cliente, e a rota recusa sem ele. Perguntar
    // assim que a tela abre encheria de erro vermelho um filtro que ninguém
    // chegou a preencher.
    enabled: filtro.empresaId !== undefined,
    staleTime: 30_000,
    // Trocar de página não pode piscar a tabela inteira em esqueleto.
    placeholderData: (anterior) => anterior,
  });
}

/**
 * O XML de uma nota, como texto, para desenhar o cupom na tela.
 *
 * Quem interpreta é o navegador: o XML já está no servidor do jeito que o
 * emissor gerou, e uma rota nova só para devolver o mesmo conteúdo em JSON
 * criaria uma segunda versão da leitura da nota — a do servidor e a daqui.
 */
export function useXmlDoArquivo(arquivoId: string | null) {
  return useQuery({
    queryKey: ['arquivo-fiscal-xml', arquivoId],
    enabled: arquivoId !== null,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const baixado = await baixarArquivo(`/arquivos-fiscais/${arquivoId ?? ''}/xml`);
      return baixado.conteudo.text();
    },
  });
}

/**
 * Manda o período em `.zip` para os e-mails do cliente e da contabilidade dele.
 *
 * A tela não escolhe destinatário, e nem poderia: quem recebe vem do cadastro,
 * e o servidor resolve a lista no instante do envio. O cliente é obrigatório —
 * "todos os clientes" não tem para quem ir.
 */
export function useEnviarArquivosPorEmail() {
  return useMutation({
    mutationFn: (filtro: FiltroArquivosFiscais & { empresaId: string }) =>
      requisitar<ResultadoEnvioPorEmail>('/arquivos-fiscais/email', {
        metodo: 'POST',
        // Campo vazio sai do corpo em vez de ir como string vazia: o schema da
        // API espera ausência, e `de` sem `ate` é recusado de propósito.
        corpo: semVazios(filtro),
      }),
  });
}

/**
 * Apaga as notas do filtro. Só administrador, e sem volta.
 *
 * Invalida o cache inteiro de arquivos fiscais, e não só a página atual: o
 * total, o resumo do rodapé e qualquer outra página da mesma competência
 * mudaram todos de uma vez.
 */
export function useExcluirArquivosFiscais() {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: (
      entrada: FiltroArquivosFiscais & { empresaId: string; motivo: string },
    ) =>
      requisitar<{ excluidos: number; razaoSocial: string }>(
        '/arquivos-fiscais/excluir',
        { metodo: 'POST', corpo: semVazios(entrada) },
      ),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: ['arquivos-fiscais'] });
    },
  });
}

/** Tira do objeto as chaves vazias ou ausentes, para o corpo do POST. */
function semVazios(filtro: object): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(filtro).filter(([, valor]) => valor !== undefined && valor !== ''),
  );
}

export interface ResultadoDownloadZip {
  readonly nome: string;
  readonly incluidos: number;
  readonly ausentes: number;
}

/**
 * Baixa o período em `.zip`. Não é `useQuery` porque não é leitura de estado
 * da tela: é uma ação com efeito no navegador, e cachear o blob só gastaria
 * memória.
 */
export function useBaixarZipDeArquivosFiscais() {
  return useMutation({
    mutationFn: async (filtro: FiltroArquivosFiscais): Promise<ResultadoDownloadZip> => {
      const baixado = await baixarArquivo('/arquivos-fiscais/zip', { ...filtro });
      salvarArquivo(baixado);

      return {
        nome: baixado.nomeSugerido,
        incluidos: Number(baixado.cabecalhos.get('x-arquivos-incluidos') ?? 0),
        ausentes: Number(baixado.cabecalhos.get('x-arquivos-ausentes') ?? 0),
      };
    },
  });
}

export interface ResultadoDownloadPlanilha {
  readonly nome: string;
  readonly incluidos: number;
}

/**
 * Baixa a relação do período em planilha.
 *
 * Mesmo filtro da tabela e, como o `.zip`, não é `useQuery`: é uma ação com
 * efeito no navegador, e guardar o arquivo em cache só gastaria memória.
 */
export function useBaixarPlanilhaDeArquivosFiscais() {
  return useMutation({
    mutationFn: async (filtro: FiltroArquivosFiscais): Promise<ResultadoDownloadPlanilha> => {
      const baixado = await baixarArquivo('/arquivos-fiscais/planilha', { ...filtro });
      salvarArquivo(baixado);

      return {
        nome: baixado.nomeSugerido,
        incluidos: Number(baixado.cabecalhos.get('x-arquivos-incluidos') ?? 0),
      };
    },
  });
}

/** Baixa o XML de uma nota isolada. */
export function useBaixarXml() {
  return useMutation({
    mutationFn: async (arquivoId: string) => {
      salvarArquivo(await baixarArquivo(`/arquivos-fiscais/${arquivoId}/xml`));
    },
  });
}

/* -------------------------------- pendências ------------------------------- */

/**
 * Como está cada cliente na competência: o que saiu e o que espera alguém.
 *
 * A competência entra na chave de cache porque ela é o eixo da tela: trocar o
 * mês é outra pergunta, e reaproveitar a resposta do mês anterior mostraria
 * "enviado" num mês que nunca foi enviado.
 */
export function useSituacaoDoMes(
  competencia: string,
  situacao: FiltroDaSituacaoMensal | undefined,
) {
  return useQuery({
    queryKey: chaves.pendencias({ competencia, situacao }),
    queryFn: ({ signal }) =>
      requisitar<SituacaoDoMes>('/pendencias', {
        parametros: { competencia, situacao },
        sinal: signal,
      }),
    staleTime: 30_000,
    placeholderData: (anterior) => anterior,
  });
}

/** O que o seletor da segunda aba oferece; ausente traz as duas. */
export type SituacaoPendente = 'pendente' | 'rejeitada';

/**
 * As notas pendentes de um cliente, uma página por vez.
 *
 * Como em arquivos fiscais, a consulta é sempre de um cliente: sem ele a tela
 * nem pergunta (`enabled`), em vez de encher o console de 400.
 */
export function useArquivosPendentes(
  filtro: {
    empresaId?: string | undefined;
    situacao?: SituacaoPendente | undefined;
    de?: string | undefined;
    ate?: string | undefined;
  },
  pagina: PaginaPedida,
) {
  return useQuery({
    queryKey: chaves.arquivosPendentes({ ...filtro, ...pagina }),
    queryFn: ({ signal }) =>
      requisitar<PaginaDeArquivosFiscais>('/pendencias/arquivos', {
        parametros: { ...filtro, ...pagina },
        sinal: signal,
      }),
    enabled: filtro.empresaId !== undefined,
    staleTime: 30_000,
    placeholderData: (anterior) => anterior,
  });
}

/**
 * As pendências do cliente agrupadas pelo código da SEFAZ.
 *
 * É a triagem antes da lista: trezentas rejeições iguais numa tarde são um
 * problema só, e a tabela crua esconde isso.
 */
export function useTriagemDePendencias(filtro: {
  empresaId?: string | undefined;
  de?: string | undefined;
  ate?: string | undefined;
  resolvida?: boolean | undefined;
}) {
  return useQuery({
    queryKey: chaves.triagem(filtro),
    queryFn: ({ signal }) =>
      requisitar<TriagemDePendencias>('/pendencias/codigos', {
        parametros: {
          empresaId: filtro.empresaId,
          de: filtro.de,
          ate: filtro.ate,
          // A querystring é texto; quem converte de volta é o schema da rota.
          resolvida: filtro.resolvida === undefined ? undefined : String(filtro.resolvida),
        },
        sinal: signal,
      }),
    enabled: filtro.empresaId !== undefined,
    staleTime: 30_000,
    placeholderData: (anterior) => anterior,
  });
}

/**
 * Registra o desfecho de um grupo de pendências.
 *
 * Invalida as três consultas da tela porque as três mudam de uma vez: a
 * triagem perde o grupo resolvido, a lista ganha o selo e a primeira aba
 * conta de novo quem ainda tem o que resolver.
 */
export function useRegistrarTratativa() {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: (entrada: {
      empresaId: string;
      de?: string | undefined;
      ate?: string | undefined;
      situacao?: SituacaoPendente | undefined;
      codigoSefaz?: string | undefined;
      desfecho: Desfecho;
      observacao?: string | undefined;
      chaveSubstituta?: string | undefined;
    }) =>
      requisitar<{ registradas: number; razaoSocial: string }>('/pendencias/tratativa', {
        metodo: 'POST',
        corpo: semVazios(entrada),
      }),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: ['pendencias'] });
      void cache.invalidateQueries({ queryKey: ['pendencias-arquivos'] });
      void cache.invalidateQueries({ queryKey: ['pendencias-codigos'] });
    },
  });
}

/**
 * Pergunta à SEFAZ o que houve com as pendências do cliente.
 *
 * A rodada tem teto e pode parar no meio — o serviço do estado sai do ar, e a
 * cota de consulta é do CNPJ da loja, o mesmo de que o caixa precisa para
 * emitir. Por isso a resposta conta o que ficou para a próxima, e a tela a
 * mostra em vez de fingir que acabou.
 */
export function useConferirNaSefaz() {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: (entrada: {
      empresaId: string;
      de?: string | undefined;
      ate?: string | undefined;
      codigoSefaz?: string | undefined;
      limite?: number | undefined;
      reconferir?: boolean | undefined;
    }) =>
      requisitar<ResultadoDaConferencia>('/pendencias/conferir', {
        metodo: 'POST',
        corpo: semVazios(entrada),
      }),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: ['pendencias'] });
      void cache.invalidateQueries({ queryKey: ['pendencias-arquivos'] });
      void cache.invalidateQueries({ queryKey: ['pendencias-codigos'] });
      // A conferência muda situação de nota, e com ela o resumo e o pacote.
      void cache.invalidateQueries({ queryKey: ['arquivos-fiscais'] });
    },
  });
}

/**
 * Os números que faltam na sequência das notas emitidas pelo cliente.
 *
 * O período é opcional: sem ele, a conferência é do acervo inteiro da loja —
 * é assim que um buraco de três meses atrás continua aparecendo.
 */
export function useSequenciaDoCliente(filtro: {
  empresaId?: string | undefined;
  de?: string | undefined;
  ate?: string | undefined;
}) {
  return useQuery({
    queryKey: chaves.sequencia(filtro),
    queryFn: ({ signal }) =>
      requisitar<SequenciaDoCliente>('/pendencias/sequencia', {
        parametros: filtro,
        sinal: signal,
      }),
    enabled: filtro.empresaId !== undefined,
    staleTime: 60_000,
    placeholderData: (anterior) => anterior,
  });
}

/* ----------------------------- clients (agentes) --------------------------- */

/**
 * Instalações do agente.
 *
 * Recarrega sozinha a cada meio minuto porque a tela responde a uma pergunta
 * que muda sem ninguém mexer nela: quais máquinas estão online agora. O sinal
 * de presença do agente chega a cada rodada, e uma lista parada mostraria um
 * "online" de dez minutos atrás.
 */
/**
 * As instalações do agente.
 *
 * `habilitado` existe para a tela de clientes não bater numa rota que o papel
 * `contador` não alcança: a listagem de instalações é operação do escritório,
 * e uma consulta que responde 403 a cada trinta segundos encheria o console
 * de erro sem nada aparecer na tela.
 */
export function useAgentes(habilitado = true) {
  return useQuery({
    enabled: habilitado,
    queryKey: chaves.agentes(),
    queryFn: ({ signal }) =>
      requisitar<Pagina<Agente>>('/agentes', { parametros: { limite: 200 }, sinal: signal }),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

/**
 * Apaga a instalação.
 *
 * É a única coisa que o painel faz com o que já existe além de bloquear: nome,
 * cliente e configuração vêm do que a instalação reportou no vínculo, e editar
 * qualquer um deles aqui daria a impressão de mudar algo que não chegaria à
 * máquina. Para corrigir, refaça o vínculo no computador do cliente.
 *
 * Os XMLs já arquivados continuam no sistema: o que some é a credencial.
 */
export function useExcluirAgente() {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => requisitar<void>(`/agentes/${id}`, { metodo: 'DELETE' }),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: chaves.agentes() });
    },
  });
}

/**
 * O que o painel precisa saber antes de oferecer o instalador: se o servidor
 * tem o executável publicado. O endereço que o agente chama não vem daqui —
 * está fixo na versão dele.
 */
export function useInstalador(habilitado = true) {
  return useQuery({
    enabled: habilitado,
    queryKey: ['instalador'],
    queryFn: ({ signal }) =>
      // A versão publicada vem junto porque é com ela que a tela diz quais
      // instalações estão desatualizadas: um hash sozinho, na linha da
      // máquina, não responderia nada.
      requisitar<{ disponivel: boolean; versaoPublicada: string | null }>(
        '/agentes/instalador',
        { sinal: signal },
      ),
    staleTime: 5 * 60_000,
  });
}

export interface ResultadoInstalador {
  readonly nome: string;
  readonly prefixoToken: string;
  /** Quantas instalações o cliente passou a ter, esta inclusive. */
  readonly instalacoesDoCliente: number;
}

/**
 * Baixa o agente já vinculado ao cliente.
 *
 * `POST` e não `GET`: a chamada **cria uma instalação** e emite a credencial
 * dela. As instalações que já existem no cliente continuam valendo — é assim
 * que uma loja com três caixas tem três agentes. O arquivo que desce é o
 * instalador, com o token dentro: é a única forma de ele chegar à máquina da
 * loja, e a única vez que ele existe fora de lá.
 */
export function useBaixarInstalador() {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: async ({ empresaId }: { empresaId: string }): Promise<ResultadoInstalador> => {
      // Sem `servidor`: o agente chama o endereço que está fixo nele, e o
      // servidor preenche o campo do `.ini` sozinho, para os agentes antigos.
      const baixado = await baixarArquivo(
        '/agentes/instalador',
        {},
        { metodo: 'POST', corpo: { empresaId } },
      );
      salvarArquivo(baixado);

      return {
        nome: baixado.nomeSugerido,
        prefixoToken: baixado.cabecalhos.get('x-instalacao-prefixo') ?? '',
        instalacoesDoCliente: Number(baixado.cabecalhos.get('x-instalacoes-cliente') ?? 1),
      };
    },
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: chaves.agentes() });
    },
  });
}

/**
 * Pede à instalação que reenvie as notas de um período.
 *
 * O pedido não sai daqui para a máquina: ele fica guardado no servidor e desce
 * na próxima presença do agente, que é de minuto em minuto. Por isso a
 * resposta volta com `varredura.entregueEm` ainda nulo — e é isso que a tela
 * mostra.
 */
export function usePedirVarredura() {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: ({ id, de, ate }: { id: string; de: string; ate: string }) =>
      requisitar<Agente>(`/agentes/${id}/varredura`, { metodo: 'POST', corpo: { de, ate } }),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: chaves.agentes() });
    },
  });
}

/**
 * Pede à instalação que troque o executável pelo que está publicado.
 *
 * Como o reenvio de período, isto não alcança a máquina agora: o pedido fica
 * guardado e desce na próxima presença. Não há versão a escolher — só existe
 * uma publicada, e é ela.
 */
export function usePedirAtualizacao() {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      requisitar<Agente>(`/agentes/${id}/atualizacao`, { metodo: 'POST' }),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: chaves.agentes() });
    },
  });
}

export function useDefinirBloqueioDoAgente() {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      bloqueado,
      motivo,
    }: {
      id: string;
      bloqueado: boolean;
      motivo?: string | undefined;
    }) =>
      requisitar<Agente>(`/agentes/${id}/bloqueio`, {
        metodo: 'PUT',
        corpo: bloqueado ? { bloqueado, motivo } : { bloqueado },
      }),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: chaves.agentes() });
    },
  });
}

/* ------------------------------ log de e-mails ----------------------------- */

export interface FiltroDeEnvios {
  readonly empresaId?: string | undefined;
  readonly situacao?: SituacaoDeEnvio | undefined;
  /**
   * `AAAA-MM`. Casa pelo campo que o fechamento grava **ou** pelo período que
   * a mensagem anunciou — o envio manual não grava competência nenhuma.
   */
  readonly competencia?: string | undefined;
}

/**
 * O log de e-mails do escritório: todos os envios, do mais recente ao mais
 * antigo.
 *
 * Recarrega sozinho de meio em meio minuto enquanto a tela está aberta: a
 * rodada do dia 5 acontece no servidor, e quem está olhando o log durante um
 * envio precisa ver as linhas aparecendo sem recarregar a página.
 */
export function useEnviosDeEmail(filtro: FiltroDeEnvios, pagina: PaginaPedida) {
  return useQuery({
    queryKey: chaves.enviosDeEmail({ ...filtro, ...pagina }),
    queryFn: ({ signal }) =>
      requisitar<PaginaDeEnvios>('/envios-de-email', {
        parametros: { ...filtro, ...pagina },
        sinal: signal,
      }),
    staleTime: 15_000,
    refetchInterval: 30_000,
    placeholderData: (anterior) => anterior,
  });
}

export function useRevogarLinks() {
  const cache = useQueryClient();

  return useMutation({
    mutationFn: (entrada: { acessoId?: string; empresaId?: string; motivo: string }) =>
      requisitar<{ revogados: number }>('/fechamentos/revogar-links', {
        metodo: 'POST',
        corpo: semVazios(entrada),
      }),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: ['envios-de-email'] });
    },
  });
}

/* -------------------------- portal público do contador --------------------- */

/**
 * As chamadas do portal não têm sessão.
 *
 * `trataProprio401` em todas elas: ali 401 significa link vencido ou revogado,
 * e quem está na página nem usuário do sistema é. Sem isto, abrir um link
 * vencido mandaria o contador para a tela de login do escritório — uma tela em
 * que ele não tem o que fazer.
 */
export function usePortal(token: string) {
  return useQuery({
    queryKey: ['portal', token],
    queryFn: ({ signal }) =>
      requisitar<DadosDoPortal>(`/publico/${token}`, { sinal: signal, trataProprio401: true }),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useArquivosDoPortal(
  token: string,
  filtro: FiltroArquivosFiscais,
  pagina: PaginaPedida,
  habilitada: boolean,
) {
  return useQuery({
    queryKey: ['portal-arquivos', token, { ...filtro, ...pagina }],
    enabled: habilitada,
    queryFn: ({ signal }) =>
      requisitar<PaginaDeArquivosFiscais>(`/publico/${token}/arquivos`, {
        parametros: { ...filtro, ...pagina },
        sinal: signal,
        trataProprio401: true,
      }),
    staleTime: 30_000,
    placeholderData: (anterior) => anterior,
  });
}

/** O `.zip` do portal traz os XMLs e a planilha juntos. */
export function useBaixarZipDoPortal(token: string) {
  return useMutation({
    mutationFn: async (filtro: FiltroArquivosFiscais): Promise<ResultadoDownloadZip> => {
      const baixado = await baixarArquivo(
        `/publico/${token}/zip`,
        { ...filtro },
        { trataProprio401: true },
      );
      salvarArquivo(baixado);

      return {
        nome: baixado.nomeSugerido,
        incluidos: Number(baixado.cabecalhos.get('x-arquivos-incluidos') ?? 0),
        ausentes: Number(baixado.cabecalhos.get('x-arquivos-ausentes') ?? 0),
      };
    },
  });
}

export function useBaixarPlanilhaDoPortal(token: string) {
  return useMutation({
    mutationFn: async (filtro: FiltroArquivosFiscais): Promise<ResultadoDownloadPlanilha> => {
      const baixado = await baixarArquivo(
        `/publico/${token}/planilha`,
        { ...filtro },
        { trataProprio401: true },
      );
      salvarArquivo(baixado);

      return {
        nome: baixado.nomeSugerido,
        incluidos: Number(baixado.cabecalhos.get('x-arquivos-incluidos') ?? 0),
      };
    },
  });
}

/** O XML de uma nota, pelo link do contador. */
export function useBaixarXmlDoPortal(token: string) {
  return useMutation({
    mutationFn: async (arquivoId: string) => {
      salvarArquivo(
        await baixarArquivo(`/publico/${token}/arquivos/${arquivoId}/xml`, {}, {
          trataProprio401: true,
        }),
      );
    },
  });
}
